import type { FAQ } from "@brookbush/types";

import { Disclosure } from "@headlessui/react";
import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDownIcon } from "lucide-react";

import SectionContainer from "./SectionContainer";
import TextBlock from "./TextBlock";

interface FaqsProps {
	faqs: FAQ[];
}

const Faqs = ({ faqs }: FaqsProps) => {
	return (
		<SectionContainer
			id="faqs"
			aria-labelledby="faqs-title"
			className="relative bg-sky-50 dark:bg-sky-700/10"
		>
			<div className="mx-auto flex max-w-screen-2xl flex-col gap-6 lg:gap-10 xl:gap-12">
				<h2 className="mx-auto text-center font-bold text-3xl tracking-tight sm:text-4xl">
					FAQs
				</h2>

				<ul className="mx-auto flex w-full max-w-3xl flex-col gap-2 sm:gap-4">
					{faqs.map((faq) => (
						<Disclosure key={faq._key}>
							{({ open }) => (
								<>
									<Disclosure.Button
										className={clsx(
											"flex w-full items-center justify-between rounded-lg px-3 py-1.5 text-left font-medium text-lg transition-colors hover:bg-sky-100/50 sm:text-xl",
											{
												"bg-sky-100/70": open,
											},
										)}
									>
										{faq.question}
										<ChevronDownIcon
											className={clsx("shrink-0 transition-transform", {
												"rotate-180 transform": open,
											})}
										/>
									</Disclosure.Button>
									<AnimatePresence>
										{open && (
											<Disclosure.Panel
												static
												as={motion.div}
												initial={{
													opacity: 0,
													y: -10,
												}}
												animate={{
													opacity: 1,
													y: 0,
													transition: {
														duration: 0.3,
													},
												}}
												exit={{
													opacity: 0,
													y: -10,
													transition: {
														duration: 0.2,
													},
												}}
											>
												<TextBlock
													value={faq.answer}
													className="sm:!text-xl -mb-3 px-4"
												/>
											</Disclosure.Panel>
										)}
									</AnimatePresence>
								</>
							)}
						</Disclosure>
					))}
				</ul>
			</div>
		</SectionContainer>
	);
};

export default Faqs;
